import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["testId", "className", "ref"];
/** @jsx jsx */
import { forwardRef, useCallback } from 'react';
import { css, jsx } from '@emotion/react';
import Avatar, { ACTIVE_SCALE_FACTOR, BORDER_WIDTH } from '@atlaskit/avatar';
import { B300, B400, B50, N0, N20, N30, N500 } from '@atlaskit/theme/colors';
var FONT_SIZE = {
  xsmall: '10px',
  small: '10px',
  medium: '11px',
  large: '12px',
  xlarge: '16px',
  xxlarge: '16px'
};
var buttonActiveStyles = css({
  // eslint-disable-next-line @atlaskit/design-system/no-nested-styles
  '&&': {
    backgroundColor: "var(--ds-background-selected, ".concat(B50, ")"),
    boxShadow: "0 0 0 ".concat(BORDER_WIDTH, "px ", "var(--ds-border-selected, ".concat(B300, ")")),
    color: "var(--ds-text-selected, ".concat(B400, ")"),
    transform: "scale(".concat(ACTIVE_SCALE_FACTOR, ")"),
    '&:hover': {
      backgroundColor: "var(--ds-background-selected-hovered, ".concat(N30, ")"),
      color: "var(--ds-text-selected, ".concat(N500, ")")
    },
    '&:active': {
      backgroundColor: "var(--ds-background-selected-pressed, ".concat(B50, ")"),
      color: "var(--ds-text-selected, ".concat(B400, ")")
    }
  }
});
var buttonStyles = css({
  // eslint-disable-next-line @atlaskit/design-system/no-nested-styles
  '&&': {
    backgroundColor: "var(--ds-background-neutral, ".concat(N20, ")"),
    color: "var(--ds-text, ".concat(N500, ")"),
    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
    fontFamily: 'inherit',
    fontWeight: "var(--ds-font-weight-medium, 500)",
    '&:hover': {
      backgroundColor: "var(--ds-background-neutral-hovered, ".concat(N30, ")"),
      color: "var(--ds-text, ".concat(N500, ")")
    },
    '&:active': {
      backgroundColor: "var(--ds-background-neutral-pressed, ".concat(B50, ")"),
      color: "var(--ds-text, ".concat(B400, ")")
    },
    '&:after': {
      display: 'none'
    }
  }
});
var MAX_DISPLAY_COUNT = 99;
var MoreIndicator = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$appearance = _ref.appearance,
    appearance = _ref$appearance === void 0 ? 'circle' : _ref$appearance,
    _ref$borderColor = _ref.borderColor,
    borderColor = _ref$borderColor === void 0 ? "var(--ds-border-inverse, ".concat(N0, ")") : _ref$borderColor,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    _ref$count = _ref.count,
    count = _ref$count === void 0 ? 0 : _ref$count,
    testId = _ref.testId,
    onClick = _ref.onClick,
    ariaControls = _ref['aria-controls'],
    ariaExpanded = _ref['aria-expanded'],
    ariaHaspopup = _ref['aria-haspopup'],
    _ref$buttonProps = _ref.buttonProps,
    buttonProps = _ref$buttonProps === void 0 ? {} : _ref$buttonProps,
    isActive = _ref.isActive;
  var onClickHander = useCallback(function (event, analyticsEvent) {
    if (buttonProps.onClick) {
      buttonProps.onClick(event);
    }
    onClick(event, analyticsEvent);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [buttonProps.onClick, onClick]);
  return jsx(Avatar, {
    appearance: appearance,
    size: size,
    borderColor: borderColor,
    ref: ref,
    onClick: onClickHander
  }, function (_ref2) {
    var _ = _ref2.testId,
      className = _ref2.className,
      ref = _ref2.ref,
      props = _objectWithoutProperties(_ref2, _excluded);
    return jsx("button", _extends({
      type: "submit"
    }, buttonProps, props, {
      ref: ref,
      "data-testid": testId,
      "aria-controls": ariaControls,
      "aria-expanded": ariaExpanded,
      "aria-haspopup": ariaHaspopup,
      style: {
        fontSize: FONT_SIZE[size]
      },
      css: [buttonStyles, isActive && buttonActiveStyles],
      className: className
    }), "+", count > MAX_DISPLAY_COUNT ? MAX_DISPLAY_COUNT : count);
  });
});
MoreIndicator.displayName = 'MoreIndicator';

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default MoreIndicator;